import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { cn } from '@prenuvo/halo';

import { adjustHeight } from '@/shared/util';

import { Textarea, TextareaProps } from './textarea.component';

interface AutoHeightTextAreaProps extends TextareaProps {
  hasError?: boolean;
}

export const AutoHeightTextArea = forwardRef<HTMLTextAreaElement, AutoHeightTextAreaProps>(
  ({ className, hasError, onChange, value, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);

      if (textareaRef.current) {
        adjustHeight(textareaRef.current);
      }
    };

    useEffect(() => {
      if (textareaRef.current) {
        adjustHeight(textareaRef.current);
      }
    }, [value]);

    return (
      <Textarea
        className={cn(
          'w-full resize-none rounded-md border-0 bg-neutral-900 text-neutral-50',
          'focus:outline-none',
          'dark:bg-neutral-900 dark:text-neutral-50',
          'custom-scrollbar overflow-hidden',
          'placeholder:text-neutral-400 dark:placeholder:text-neutral-400',
          'disabled:text-semantic-text-disabled disabled:pointer-events-none',
          { 'border-red-500': hasError },
          className,
        )}
        onChange={handleChange}
        ref={textareaRef}
        value={value}
        {...props}
      />
    );
  },
);

AutoHeightTextArea.displayName = 'AutoHeightTextArea';
